/* ------------------------------------------------------------------------- *
 * Idenza — Stablecoin program risk console (sponsor bank demo screen)
 *
 * Self contained. One file, one default export, no local imports, no CSS
 * import, no Tailwind, no build step. Runtime deps are recharts and
 * react-icons and recharts only. react-icons is already in the Navigator
 * bundle, so recharts is the one dependency to check for.
 *
 *   import StablecoinRiskConsole from "./StablecoinRiskConsole";
 *   <StablecoinRiskConsole />            // renders fully populated from DEMO_DATA
 *
 * To point it at live data, pass `data` in the same shape as DEMO_DATA below
 * and hand it the callbacks: onPeriodChange, onWeightsChange, onHoldAction,
 * onSelectCounterparty, onOpenEvidence.
 * ------------------------------------------------------------------------- */

import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell,
} from "recharts";
import {
  FiShield, FiCreditCard, FiLayers, FiClock, FiArrowUpRight, FiArrowDownRight,
  FiMinus, FiLock, FiCheckCircle, FiChevronRight, FiActivity, FiFileText,
} from "react-icons/fi";

/* =============================== tokens =============================== */
const theme = {
  ink: "#0D1729",
  inkSoft: "#1B2A44",
  paper: "#F6F7FA",
  card: "#FFFFFF",
  line: "#E3E7EF",
  lineSoft: "#EDF0F6",
  text: "#16223A",
  muted: "#6A7789",

  accent: "#0E5F63",
  accentSoft: "#E3F0F0",

  red: "#A32218",
  redSoft: "#FBEAE8",
  amber: "#B4690E",
  amberSoft: "#FDF1E1",
  green: "#1A6B43",
  greenSoft: "#E6F2EB",
  violet: "#5B4B8A",

  headerFg: "#FFFFFF",
  headerMuted: "#9FB1CC",
  headerEyebrow: "#7C93B8",

  sans: 'ui-sans-serif, system-ui, "Segoe UI", Helvetica, Arial, sans-serif',
  mono: 'ui-mono, ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
};

/* Risk score bands. Higher is more risk, deliberately: a program scoring 71
 * should never read as healthier than one scoring 32. */
const BANDS = [
  { min: 75, label: "Critical", key: "red" },
  { min: 50, label: "Elevated", key: "amber" },
  { min: 25, label: "Moderate", key: "accent" },
  { min: 0, label: "Low", key: "green" },
];

function bandFor(score) {
  return BANDS.find((b) => score >= b.min) || BANDS[BANDS.length - 1];
}
function scoreColor(score, t = theme) {
  return t[bandFor(score).key];
}
function scoreBand(score) {
  return bandFor(score).label;
}

/* ========================= scoped stylesheet ========================== */
const STYLE_ID = "idz-stablecoin-risk-styles";

const css = `
.idz-root { box-sizing: border-box; }
.idz-root *, .idz-root *::before, .idz-root *::after { box-sizing: inherit; }

.idz-shell { max-width: 1680px; margin: 0 auto; padding: 0 20px; }
.idz-main { padding-top: 20px; padding-bottom: 24px; }

/* header */
.idz-topbar { display: flex; flex-wrap: wrap; align-items: center; gap: 16px; padding-top: 16px; padding-bottom: 16px; }
.idz-topbar-actions { margin-left: auto; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }

/* grids */
.idz-kpis { display: grid; gap: 12px; grid-template-columns: repeat(6, minmax(0, 1fr)); }
.idz-cols { display: grid; gap: 16px; grid-template-columns: 2fr 1fr; margin-top: 16px; }
.idz-cols-even { display: grid; gap: 16px; grid-template-columns: 1fr 1fr; }
.idz-stack { display: grid; gap: 16px; align-content: start; }
.idz-block { margin-top: 16px; }

/* grid children default to min-width:auto, which lets a wide table push the
 * page sideways instead of scrolling inside its own card */
.idz-kpis > *, .idz-cols > *, .idz-cols-even > *, .idz-stack > * { min-width: 0; }

@media (max-width: 1280px) {
  .idz-kpis { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .idz-cols { grid-template-columns: 1fr; }
}
@media (max-width: 760px) {
  .idz-kpis { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .idz-cols-even { grid-template-columns: 1fr; }
  .idz-shell { padding: 0 12px; }
}

/* card */
.idz-card { border: 1px solid var(--idz-line); background: var(--idz-card); border-radius: 10px; }
.idz-card-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px;
  padding: 12px 16px; border-bottom: 1px solid var(--idz-line-soft); }
.idz-card-body { padding: 16px; }
.idz-card-body-flush { padding: 0; }

/* type */
.idz-eyebrow { text-transform: uppercase; font-size: 10.5px; letter-spacing: 0.12em; font-weight: 700; }
.idz-h2 { font-size: 13.5px; font-weight: 700; letter-spacing: -0.01em; margin: 0; }
.idz-note { font-size: 11.5px; margin: 2px 0 0; }
.idz-figure { font-variant-numeric: tabular-nums; letter-spacing: -0.02em; font-weight: 600; }

/* controls */
.idz-seg { display: flex; padding: 2px; border-radius: 8px; }
.idz-seg button { border: 0; border-radius: 6px; padding: 4px 12px; font-size: 12px; font-weight: 600;
  cursor: pointer; background: transparent; }
.idz-btn { border: 0; border-radius: 6px; padding: 5px 10px; font-size: 11.5px; font-weight: 700; cursor: pointer; }
.idz-btn-ghost { border: 1px solid var(--idz-line); background: transparent; border-radius: 6px;
  padding: 4px 10px; font-size: 11.5px; font-weight: 600; cursor: pointer; }
.idz-root button:focus-visible, .idz-root input:focus-visible, .idz-root [tabindex]:focus-visible {
  outline: 2px solid var(--idz-accent); outline-offset: 2px; }
.idz-range { width: 100%; margin: 0; display: block; accent-color: var(--idz-accent); }

/* tables */
.idz-scroll { overflow-x: auto; }
.idz-table { width: 100%; border-collapse: collapse; }
.idz-table th { text-align: left; padding: 8px 16px; background: var(--idz-paper); font-weight: 700; }
.idz-table td { padding: 10px 16px; border-top: 1px solid var(--idz-line-soft); vertical-align: middle; }
.idz-table tbody tr:hover td { background: var(--idz-paper); }
.idz-num { text-align: right; font-variant-numeric: tabular-nums; }

/* proximity grid */
.idz-matrix { border-collapse: separate; border-spacing: 2px; width: 100%; min-width: 560px; }
.idz-matrix th { padding: 0 0 4px; }
.idz-cell { width: 100%; border-radius: 5px; padding: 9px 4px; font-size: 11.5px; font-weight: 600;
  border: 2px solid transparent; cursor: pointer; font-variant-numeric: tabular-nums;
  transition: transform 120ms ease; }
.idz-cell:hover { transform: translateY(-1px); }
.idz-cell[aria-pressed="true"] { border-color: var(--idz-ink); }

/* meters */
.idz-meter { height: 8px; width: 100%; border-radius: 4px; background: var(--idz-line-soft); overflow: hidden; }
.idz-meter > span { display: block; height: 100%; border-radius: 4px; }
.idz-meter-sm { height: 6px; }

/* states */
.idz-empty { padding: 40px 16px; text-align: center; }
.idz-skeleton { background: linear-gradient(90deg, var(--idz-line-soft) 25%, var(--idz-line) 37%, var(--idz-line-soft) 63%);
  background-size: 400% 100%; animation: idz-shimmer 1.4s ease infinite; border-radius: 6px; }
@keyframes idz-shimmer { 0% { background-position: 100% 0 } 100% { background-position: 0 0 } }

@media (prefers-reduced-motion: reduce) {
  .idz-root *, .idz-root *::before, .idz-root *::after {
    animation-duration: 0.01ms !important; animation-iteration-count: 1 !important; transition-duration: 0.01ms !important; }
}

@media print {
  .idz-no-print { display: none !important; }
  .idz-card { break-inside: avoid; }
}
`;

function ensureStyles(doc) {
  if (typeof document === "undefined" && !doc) return;
  const d = doc || document;
  if (d.getElementById(STYLE_ID)) return;
  const el = d.createElement("style");
  el.id = STYLE_ID;
  el.textContent = css;
  d.head.appendChild(el);
}

/* ============================= demo data ============================== */
const DEMO_DATA = {
  meta: {
    bank: "Sponsor bank",
    policyVersion: "v4.2",
    coveragePct: 100,
    asOf: "2026-07-27T23:59:00Z",
  },

  kpisByPeriod: {
    "7d":  { volume: 96.4,   wallets: 4820,  sanctions: 2,  directExposure: 0.28, heldCount: 11,  heldValue: 0.7, medianDecisionMins: 36 },
    "30d": { volume: 412.8,  wallets: 18942, sanctions: 7,  directExposure: 1.24, heldCount: 43,  heldValue: 2.9, medianDecisionMins: 41 },
    "90d": { volume: 1187.3, wallets: 51204, sanctions: 19, directExposure: 3.61, heldCount: 128, heldValue: 8.4, medianDecisionMins: 47 },
  },

  /* dims are 0-100 sub-scores; the console applies the weights */
  programs: [
    { id: "gp",    name: "Global payouts",             asset: "USDC",  volume: 214.6, thinData: false,
      dims: { sanctions: 12, exposure: 31, velocity: 44, counterparty: 28, controls: 18 } },
    { id: "latam", name: "LatAm corridor",             asset: "USDT",  volume: 118.2, thinData: false,
      dims: { sanctions: 34, exposure: 66, velocity: 71, counterparty: 58, controls: 40 } },
    { id: "treas", name: "Treasury settlement",        asset: "PYUSD", volume: 68.9,  thinData: false,
      dims: { sanctions: 4,  exposure: 9,  velocity: 16, counterparty: 12, controls: 11 } },
    { id: "mrch",  name: "Merchant settlement (pilot)", asset: "USDC", volume: 11.1,  thinData: true,
      dims: { sanctions: 8,  exposure: 22, velocity: 19, counterparty: 25, controls: 34 } },
  ],

  defaultWeights: { sanctions: 30, exposure: 25, velocity: 15, counterparty: 20, controls: 10 },

  scoreTrend: [
    { period: "Feb", gp: 22, latam: 41, treas: 9,  mrch: 14 },
    { period: "Mar", gp: 24, latam: 44, treas: 10, mrch: 16 },
    { period: "Apr", gp: 27, latam: 52, treas: 9,  mrch: 18 },
    { period: "May", gp: 26, latam: 57, treas: 11, mrch: 21 },
    { period: "Jun", gp: 29, latam: 61, treas: 10, mrch: 20 },
    { period: "Jul", gp: 31, latam: 66, treas: 9,  mrch: 23 },
  ],

  /* exposure in USD thousands, indexed by hop distance */
  hops: ["Direct", "1 hop", "2 hops", "3+ hops"],
  exposureMatrix: [
    { category: "Sanctioned entity (SDN)", values: [186, 412, 96, 24] },
    { category: "Mixer / tumbler",         values: [64, 903, 1240, 388] },
    { category: "Darknet market",          values: [0, 118, 344, 512] },
    { category: "Ransomware proceeds",     values: [0, 46, 132, 208] },
    { category: "Scam & fraud proceeds",   values: [212, 664, 890, 1104] },
    { category: "Unlicensed gambling",     values: [96, 284, 612, 745] },
    { category: "High-risk VASP",          values: [418, 1320, 1660, 2210] },
    { category: "Unhosted / unattributed", values: [1240, 2180, 1420, 980] },
  ],

  screeningFunnel: [
    { label: "Transfers settled",    count: 128430, pct: 100 },
    { label: "Screened",             count: 128430, pct: 100 },
    { label: "Alerted",              count: 1842,   pct: 1.43 },
    { label: "Escalated to review",  count: 306,    pct: 0.24 },
    { label: "Payout held",          count: 43,     pct: 0.03 },
    { label: "SAR referred",         count: 11,     pct: 0.01 },
  ],

  travelRule: [
    { label: "Originator data complete",      pct: 98.2 },
    { label: "Beneficiary data complete",     pct: 91.4 },
    { label: "Counterparty VASP identified",  pct: 87.0 },
    { label: "Unhosted counterparties",       pct: 12.6, invert: true },
  ],

  typologies: [
    { name: "Rapid pass-through",          alerts: 214, escalated: 38 },
    { name: "Mixer exposure",              alerts: 168, escalated: 61 },
    { name: "Peel chain",                  alerts: 141, escalated: 27 },
    { name: "Cross-chain bridge hop",      alerts: 129, escalated: 19 },
    { name: "Structuring below threshold", alerts: 96,  escalated: 22 },
    { name: "New wallet, high value",      alerts: 88,  escalated: 31 },
    { name: "Sanctioned counterparty",     alerts: 24,  escalated: 24 },
    { name: "Dormant wallet reactivation", alerts: 19,  escalated: 6 },
  ],

  chains: [
    { name: "Ethereum", value: 186.4 },
    { name: "Tron",     value: 118.2 },
    { name: "Base",     value: 58.7 },
    { name: "Solana",   value: 33.1 },
    { name: "Polygon",  value: 16.4 },
  ],

  assets: [
    { name: "USDC",  pct: 58 },
    { name: "USDT",  pct: 31 },
    { name: "PYUSD", pct: 11 },
  ],

  counterparties: [
    { id: "cp1", name: "Verdant Exchange Ltd",   type: "VASP",      jurisdiction: "SG",      chain: "Ethereum", volume: 42.8, taintedPct: 0.41, score: 58, trend: 6,  posture: "Watch" },
    { id: "cp2", name: "Onda Cambio SA",         type: "VASP",      jurisdiction: "AR",      chain: "Tron",     volume: 31.2, taintedPct: 1.92, score: 74, trend: 11, posture: "Restricted" },
    { id: "cp3", name: "Halcyon Treasury LLC",   type: "Corporate", jurisdiction: "US",      chain: "Ethereum", volume: 28.6, taintedPct: 0.02, score: 12, trend: -3, posture: "Cleared" },
    { id: "cp4", name: "0x7f3a…c418 (unhosted)", type: "Unhosted",  jurisdiction: "Unknown", chain: "Base",     volume: 14.9, taintedPct: 3.44, score: 81, trend: 14, posture: "Held" },
    { id: "cp5", name: "Nimbus Pay OU",          type: "VASP",      jurisdiction: "EE",      chain: "Solana",   volume: 12.4, taintedPct: 0.66, score: 47, trend: 0,  posture: "Watch" },
    { id: "cp6", name: "Cordillera Remesas",     type: "MSB",       jurisdiction: "MX",      chain: "Tron",     volume: 9.8,  taintedPct: 0.28, score: 39, trend: -2, posture: "Cleared" },
  ],

  holds: [
    { id: "H-4471", counterparty: "0x7f3a…c418",        programId: "latam", program: "LatAm corridor",             asset: "USDT", chain: "Tron",     amountUsd: 412500, reason: "Mixer exposure, 1 hop",       category: "Mixer / tumbler",         hop: 1,    age: "2h 14m",  score: 81 },
    { id: "H-4468", counterparty: "Onda Cambio SA",     programId: "latam", program: "LatAm corridor",             asset: "USDT", chain: "Tron",     amountUsd: 288000, reason: "Rapid pass-through",          category: "High-risk VASP",          hop: 2,    age: "5h 02m",  score: 74 },
    { id: "H-4463", counterparty: "0x91be…772d",        programId: "gp",    program: "Global payouts",             asset: "USDC", chain: "Base",     amountUsd: 196400, reason: "SDN match, direct",           category: "Sanctioned entity (SDN)", hop: 0,    age: "7h 41m",  score: 96 },
    { id: "H-4459", counterparty: "Verdant Exchange Ltd", programId: "gp",  program: "Global payouts",             asset: "USDC", chain: "Ethereum", amountUsd: 121900, reason: "Beneficiary data incomplete", category: null,                      hop: null, age: "11h 08m", score: 58 },
    { id: "H-4455", counterparty: "0x2c40…9a1f",        programId: "mrch",  program: "Merchant settlement (pilot)", asset: "USDC", chain: "Solana",  amountUsd: 64200,  reason: "New wallet, high value",      category: "Unhosted / unattributed", hop: 3,    age: "16h 22m", score: 63 },
  ],
};

/* ------------------------------------------------------------------ *
 * StablecoinRiskConsole — presentational. Owns no data fetching.
 *
 * props
 *   data            view model in the DEMO_DATA shape. Defaults to DEMO_DATA.
 *   period          "7d" | "30d" | "90d". Uncontrolled if omitted.
 *   onPeriodChange  (period) => void
 *   weights         { sanctions, exposure, velocity, counterparty, controls }
 *   onWeightsChange (weights) => void   omit to make the sliders read-only
 *   onHoldAction    ({ hold, action }) => void   action: "release" | "escalate"
 *   onSelectCounterparty (counterparty) => void
 *   onOpenEvidence  () => void
 *   theme           token overrides, merged over the built-in palette
 *   showHeader      false when Navigator's PageHeader already renders the title
 * ------------------------------------------------------------------ */

const PERIODS = ["7d", "30d", "90d"];
const DIMENSIONS = [
  { key: "sanctions", label: "Sanctions" },
  { key: "exposure", label: "Illicit exposure" },
  { key: "velocity", label: "Velocity" },
  { key: "counterparty", label: "Counterparty" },
  { key: "controls", label: "Control gaps" },
];

const usd = (n, d = 1) => (n >= 1000 ? `$${(n / 1000).toFixed(d)}B` : `$${n.toFixed(d)}M`);
const money = (n) => "$" + Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 });
const num = (n) => Number(n).toLocaleString("en-US");

/* ------------------------------- atoms -------------------------------- */
function Eyebrow({ children, color, t }) {
  return <div className="idz-eyebrow" style={{ color: color || t.muted, fontFamily: t.sans }}>{children}</div>;
}

function Card({ t, title, note, right, children, flush = false, className = "" }) {
  return (
    <section className={`idz-card ${className}`}>
      {(title || right) && (
        <header className="idz-card-head">
          <div>
            <h2 className="idz-h2" style={{ fontFamily: t.sans, color: t.text }}>{title}</h2>
            {note && <p className="idz-note" style={{ fontFamily: t.sans, color: t.muted }}>{note}</p>}
          </div>
          {right}
        </header>
      )}
      <div className={flush ? "idz-card-body-flush" : "idz-card-body"}>{children}</div>
    </section>
  );
}

function Pill({ children, tone = "neutral", t }) {
  const map = {
    neutral: [t.lineSoft, t.muted],
    accent: [t.accentSoft, t.accent],
    red: [t.redSoft, t.red],
    amber: [t.amberSoft, t.amber],
    green: [t.greenSoft, t.green],
  };
  const [bg, fg] = map[tone] || map.neutral;
  return (
    <span style={{ background: bg, color: fg, fontFamily: t.sans, fontSize: 10.5, fontWeight: 700,
      letterSpacing: "0.04em", padding: "2px 8px", borderRadius: 5, whiteSpace: "nowrap" }}>
      {children}
    </span>
  );
}

function Delta({ v, t, invert = false }) {
  const flat = v === 0;
  const bad = invert ? v < 0 : v > 0;
  const color = flat ? t.muted : bad ? t.red : t.green;
  const Icon = flat ? FiMinus : v > 0 ? FiArrowUpRight : FiArrowDownRight;
  return (
    <span style={{ color, fontFamily: t.mono, fontSize: 11.5, display: "inline-flex", alignItems: "center", gap: 3 }}>
      <Icon size={12} aria-hidden="true" />
      {flat ? "0" : `${v > 0 ? "+" : ""}${v}`}
    </span>
  );
}

function Meter({ pct, color, t, small = false }) {
  return (
    <div className={`idz-meter${small ? " idz-meter-sm" : ""}`} style={{ marginTop: 4 }}>
      <span style={{ width: `${Math.max(0, Math.min(100, pct))}%`, background: color || t.accent }} />
    </div>
  );
}

function ChartTip({ active, payload, label, unit = "", t }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div style={{ background: t.card, border: `1px solid ${t.line}`, borderRadius: 6, padding: "8px 12px",
      boxShadow: "0 6px 18px rgba(13,23,41,0.10)" }}>
      {label && <div style={{ fontFamily: t.sans, fontSize: 11, color: t.muted, marginBottom: 4 }}>{label}</div>}
      {payload.map((p) => (
        <div key={p.name} style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: t.mono, fontSize: 12, color: t.text }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: p.color || p.fill }} />
          <span style={{ fontFamily: t.sans, fontSize: 11.5, color: t.muted }}>{p.name}</span>
          <span style={{ marginLeft: "auto", fontWeight: 600 }}>{p.value}{unit}</span>
        </div>
      ))}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub, tone, t }) {
  return (
    <div className="idz-card" style={{ padding: "12px 16px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, color: t.muted }}>
        <Icon size={13} aria-hidden="true" />
        <Eyebrow t={t}>{label}</Eyebrow>
      </div>
      <div className="idz-figure" style={{ fontFamily: t.mono, fontSize: 25, color: tone || t.text, marginTop: 6 }}>
        {value}
      </div>
      <div style={{ fontFamily: t.sans, fontSize: 11.5, color: t.muted, marginTop: 2 }}>{sub}</div>
    </div>
  );
}

/* -------------------- signature: exposure proximity ------------------- */
function ProximityGrid({ data, selected, onSelect, t }) {
  const rows = data.exposureMatrix;
  const hops = data.hops;
  const max = Math.max(...rows.flatMap((r) => r.values), 1);
  const total = rows.reduce((a, r) => a + r.values.reduce((x, y) => x + y, 0), 0);
  const direct = rows.reduce((a, r) => a + r.values[0], 0);

  const shade = (v, col) => {
    if (!v) return { bg: t.lineSoft, fg: "transparent" };
    const k = Math.pow(v / max, 0.55);
    const base = col === 0 ? [163, 34, 24] : col === 1 ? [180, 105, 14] : [14, 95, 99];
    const alpha = 0.1 + k * 0.85;
    return { bg: `rgba(${base[0]},${base[1]},${base[2]},${alpha})`, fg: alpha > 0.55 ? "#FFFFFF" : t.text };
  };

  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 24, marginBottom: 16 }}>
        <div>
          <Eyebrow t={t}>Total tainted exposure</Eyebrow>
          <div className="idz-figure" style={{ fontFamily: t.mono, fontSize: 30, color: t.text }}>
            ${(total / 1000).toFixed(2)}M
          </div>
        </div>
        <div>
          <Eyebrow t={t} color={t.red}>Direct contact</Eyebrow>
          <div className="idz-figure" style={{ fontFamily: t.mono, fontSize: 30, color: t.red }}>
            ${(direct / 1000).toFixed(2)}M
          </div>
        </div>
        <p style={{ fontFamily: t.sans, fontSize: 11.5, color: t.muted, lineHeight: 1.5, maxWidth: 360, margin: 0 }}>
          Value settled with counterparties whose funds trace back to each category, by how many
          transfers separate them from the source. Select a cell to filter the hold queue.
        </p>
      </div>

      <div className="idz-scroll">
        <table className="idz-matrix">
          <caption style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>
            Exposure by category and hop distance, in US dollar thousands
          </caption>
          <thead>
            <tr>
              <th style={{ width: "34%", background: "transparent" }} />
              {hops.map((h, i) => (
                <th key={h} style={{ textAlign: "center", background: "transparent" }}>
                  <Eyebrow t={t} color={i === 0 ? t.red : t.muted}>{h}</Eyebrow>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.category}>
                <td style={{ padding: "0 12px 0 0", border: 0, fontFamily: t.sans, fontSize: 12, color: t.text, whiteSpace: "nowrap" }}>
                  {row.category}
                </td>
                {row.values.map((v, ci) => {
                  const s = shade(v, ci);
                  const on = selected && selected.category === row.category && selected.col === ci;
                  return (
                    <td key={ci} style={{ padding: 0, border: 0 }}>
                      <button
                        type="button"
                        className="idz-cell"
                        aria-pressed={on ? "true" : "false"}
                        aria-label={`${row.category}, ${hops[ci]}, ${v} thousand dollars`}
                        onClick={() => onSelect(on ? null : { category: row.category, col: ci, value: v })}
                        style={{ background: s.bg, color: s.fg, fontFamily: t.mono }}
                      >
                        {v === 0 ? "—" : `${v}k`}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* -------------------- risk appetite / weights ------------------------- */
function AppetitePanel({ weights, onWeightsChange, scores, t }) {
  const sum = Object.values(weights).reduce((a, b) => a + b, 0) || 1;
  const readOnly = typeof onWeightsChange !== "function";
  return (
    <div className="idz-cols-even">
      <div>
        <Eyebrow t={t}>{readOnly ? "Weights in force" : "Weights, set by the bank"}</Eyebrow>
        <div style={{ marginTop: 12, display: "grid", gap: 12 }}>
          {DIMENSIONS.map((d) => (
            <div key={d.key}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                <label htmlFor={`idz-w-${d.key}`} style={{ fontFamily: t.sans, fontSize: 12, color: t.text }}>{d.label}</label>
                <span style={{ fontFamily: t.mono, fontSize: 11.5, color: t.muted }}>
                  {Math.round((weights[d.key] / sum) * 100)}%
                </span>
              </div>
              <input
                id={`idz-w-${d.key}`}
                className="idz-range"
                type="range" min={0} max={40} value={weights[d.key]} disabled={readOnly}
                onChange={(e) => onWeightsChange({ ...weights, [d.key]: Number(e.target.value) })}
                style={{ marginTop: 4 }}
              />
            </div>
          ))}
        </div>
        <p style={{ fontFamily: t.sans, fontSize: 11, color: t.muted, lineHeight: 1.5, marginTop: 12 }}>
          Changes are versioned and take effect on the next scoring run. The score is the weighted
          sum of the five dimensions shown, so there is nothing hidden behind it.
        </p>
      </div>

      <div>
        <Eyebrow t={t}>Program risk score, higher is more risk</Eyebrow>
        <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
          {scores.map((p) => (
            <div key={p.id} style={{ border: `1px solid ${t.lineSoft}`, background: t.paper, borderRadius: 8, padding: "8px 12px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                <div style={{ fontFamily: t.sans, fontSize: 12.5, fontWeight: 600, color: t.text }}>
                  {p.name}<span style={{ color: t.muted, fontWeight: 400 }}> · {p.asset}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {p.thinData && <Pill t={t}>Insufficient data</Pill>}
                  <span className="idz-figure" style={{ fontFamily: t.mono, fontSize: 17, color: p.thinData ? t.muted : scoreColor(p.score, t) }}>
                    {p.score}
                  </span>
                </div>
              </div>
              <Meter pct={p.score} color={p.thinData ? t.muted : scoreColor(p.score, t)} t={t} small />
              <div style={{ fontFamily: t.sans, fontSize: 11, color: t.muted, marginTop: 4 }}>
                {p.thinData ? "Below the volume floor for scoring" : `${scoreBand(p.score)} · ${usd(p.volume)} settled`}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------- main -------------------------------- */
export default function StablecoinRiskConsole({
  data = DEMO_DATA,
  period: periodProp,
  onPeriodChange,
  weights: weightsProp,
  onWeightsChange,
  onHoldAction,
  onSelectCounterparty,
  onOpenEvidence,
  theme: themeOverride,
  showHeader = true,
}) {
  const t = useMemo(() => ({ ...theme, ...(themeOverride || {}) }), [themeOverride]);
  useEffect(() => { ensureStyles(); }, []);

  const [periodState, setPeriodState] = useState(periodProp || "30d");
  const period = periodProp || periodState;
  const setPeriod = (p) => { setPeriodState(p); if (onPeriodChange) onPeriodChange(p); };

  const [weightsState, setWeightsState] = useState(weightsProp || data.defaultWeights);
  const weights = weightsProp || weightsState;
  const setWeights = (w) => {
    if (onWeightsChange) onWeightsChange(w);
    if (!weightsProp) setWeightsState(w);
  };
  const weightsEditable = !weightsProp || typeof onWeightsChange === "function";

  const [cell, setCell] = useState(null);
  const [resolved, setResolved] = useState([]);

  const k = data.kpisByPeriod[period] || Object.values(data.kpisByPeriod)[0];

  const scores = useMemo(() => {
    const sum = Object.values(weights).reduce((a, b) => a + b, 0) || 1;
    return data.programs
      .map((p) => ({
        ...p,
        score: Math.round(DIMENSIONS.reduce((acc, d) => acc + (weights[d.key] / sum) * p.dims[d.key], 0)),
      }))
      .sort((a, b) => b.score - a.score);
  }, [data.programs, weights]);

  const chainColors = [t.accent, t.amber, t.inkSoft, t.violet, "#8FA3B8"];
  const totalChain = data.chains.reduce((a, c) => a + c.value, 0);

  const openHolds = data.holds.filter((h) => !resolved.includes(h.id));
  const visibleHolds = cell
    ? openHolds.filter((h) => h.category === cell.category && h.hop === cell.col)
    : openHolds;

  const act = (hold, action) => {
    setResolved((r) => [...r, hold.id]);
    if (onHoldAction) onHoldAction({ hold, action });
  };

  const trendSeries = [
    { key: "latam", label: "LatAm corridor", color: t.red, dash: null, width: 2.5 },
    { key: "gp", label: "Global payouts", color: t.accent, dash: null, width: 2.5 },
    { key: "mrch", label: "Merchant pilot", color: t.violet, dash: "4 3", width: 2 },
    { key: "treas", label: "Treasury", color: t.green, dash: null, width: 2 },
  ];

  const cssVars = {
    "--idz-line": t.line, "--idz-line-soft": t.lineSoft, "--idz-card": t.card,
    "--idz-paper": t.paper, "--idz-accent": t.accent, "--idz-ink": t.ink,
  };

  return (
    <div className="idz-root" style={{ ...cssVars, background: t.paper, color: t.text, fontFamily: t.sans, minHeight: "100%" }}>
      {showHeader && (
        <header style={{ background: t.ink, margin: "0 20px" }}>
          <div className="idz-shell idz-topbar">
            <div>
              <div className="idz-eyebrow" style={{ color: t.headerEyebrow, letterSpacing: "0.16em" }}>
                IDENZA · STABLECOIN PROGRAM OVERSIGHT
              </div>
              <h1 style={{ fontSize: 24, fontWeight: 800, color: t.headerFg, letterSpacing: "-0.02em", margin: "2px 0 0" }}>
                Wallet screening, KYT and exposure
              </h1>
            </div>
            <div className="idz-topbar-actions idz-no-print">
              <div className="idz-seg" style={{ background: t.inkSoft }} role="group" aria-label="Reporting period">
                {PERIODS.map((p) => (
                  <button key={p} onClick={() => setPeriod(p)} aria-pressed={period === p}
                    style={{ fontFamily: t.mono, background: period === p ? "#FFFFFF" : "transparent",
                      color: period === p ? t.ink : t.headerMuted }}>
                    {p}
                  </button>
                ))}
              </div>
              <span style={{ background: t.inkSoft, color: t.headerMuted, fontFamily: t.mono, fontSize: 11.5,
                padding: "5px 10px", borderRadius: 6 }}>
                Screening coverage {data.meta.coveragePct}%
              </span>
            </div>
          </div>
        </header>
      )}

      <main className="idz-shell idz-main">
        <div className="idz-kpis">
          <Kpi t={t} icon={FiLayers} label="Settled volume" value={usd(k.volume)} sub={`${period} across ${data.programs.length} programs`} />
          <Kpi t={t} icon={FiCreditCard} label="Wallets screened" value={num(k.wallets)} sub="Pre-settlement, every transfer" />
          <Kpi t={t} icon={FiShield} label="Sanctions hits" value={k.sanctions} sub="OFAC SDN, confirmed" tone={t.red} />
          <Kpi t={t} icon={FiActivity} label="Direct illicit exposure" value={`$${k.directExposure.toFixed(2)}M`}
            sub={`${((k.directExposure / k.volume) * 100).toFixed(2)}% of settled value`} tone={t.amber} />
          <Kpi t={t} icon={FiLock} label="Payouts held" value={k.heldCount} sub={`${usd(k.heldValue)} withheld pending review`} />
          <Kpi t={t} icon={FiClock} label="Median time to decision" value={`${k.medianDecisionMins}m`} sub="Alert raised to disposition" />
        </div>

        <div className="idz-cols">
          <Card t={t} title="Exposure proximity" note="Where tainted value sits, and how far it is from the source">
            <ProximityGrid data={data} selected={cell} onSelect={setCell} t={t} />
          </Card>

          <div className="idz-stack">
            <Card t={t} title="Screening funnel" note={`${period}, all programs`}>
              <div style={{ display: "grid", gap: 8 }}>
                {data.screeningFunnel.map((f, i) => (
                  <div key={f.label}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                      <span style={{ fontSize: 12 }}>{f.label}</span>
                      <span className="idz-figure" style={{ fontFamily: t.mono, fontSize: 12 }}>{num(f.count)}</span>
                    </div>
                    <Meter t={t} pct={Math.max(Math.pow(f.pct / 100, 0.35) * 100, 3)}
                      color={i >= 4 ? t.red : i >= 2 ? t.amber : t.accent} />
                  </div>
                ))}
              </div>
            </Card>

            <Card t={t} title="Travel rule completeness" note="Counterparty data on outbound transfers">
              <div style={{ display: "grid", gap: 12 }}>
                {data.travelRule.map((r) => {
                  const c = r.invert ? t.violet : r.pct >= 95 ? t.green : t.amber;
                  return (
                    <div key={r.label}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                        <span style={{ fontSize: 12 }}>{r.label}</span>
                        <span className="idz-figure" style={{ fontFamily: t.mono, fontSize: 12.5, color: c }}>{r.pct}%</span>
                      </div>
                      <Meter t={t} pct={r.pct} color={c} small />
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>
        </div>

        <div className="idz-cols">
          <Card t={t} title="Risk appetite and program scoring"
            note={weightsEditable ? "Move a weight and the ranking recalculates. The bank owns the policy." : "Weights are set by policy and shown for transparency."}
            right={<Pill t={t} tone="accent">Policy {data.meta.policyVersion}</Pill>}>
            <AppetitePanel weights={weights} onWeightsChange={weightsEditable ? setWeights : undefined} scores={scores} t={t} />
          </Card>

          <Card t={t} title="Score trend" note="Six months, higher is more risk">
            <div style={{ height: 232 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.scoreTrend} margin={{ top: 6, right: 8, bottom: 0, left: -18 }}>
                  <CartesianGrid stroke={t.lineSoft} vertical={false} />
                  <XAxis dataKey="period" tick={{ fill: t.muted, fontSize: 11 }} axisLine={{ stroke: t.line }} tickLine={false} />
                  <YAxis domain={[0, 80]} tick={{ fill: t.muted, fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip content={<ChartTip t={t} />} />
                  {trendSeries.map((s) => (
                    <Line key={s.key} type="monotone" dataKey={s.key} name={s.label} stroke={s.color}
                      strokeWidth={s.width} strokeDasharray={s.dash || undefined} dot={false} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 16px", marginTop: 8 }}>
              {trendSeries.map((s) => (
                <span key={s.key} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, color: t.muted }}>
                  <span style={{ width: 9, height: 3, borderRadius: 2, background: s.color }} />{s.label}
                </span>
              ))}
            </div>
          </Card>
        </div>

        <div className="idz-cols">
          <Card t={t} title="KYT alerts by typology" note={`${period} · escalations shown against alert volume`}>
            <div style={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.typologies} layout="vertical" margin={{ top: 4, right: 16, bottom: 0, left: 8 }} barCategoryGap={7}>
                  <CartesianGrid stroke={t.lineSoft} horizontal={false} />
                  <XAxis type="number" tick={{ fill: t.muted, fontSize: 11 }} axisLine={{ stroke: t.line }} tickLine={false} />
                  <YAxis type="category" dataKey="name" width={168} tick={{ fill: t.text, fontSize: 11.5 }} axisLine={false} tickLine={false} />
                  <Tooltip content={<ChartTip t={t} />} cursor={{ fill: "rgba(14,95,99,0.05)" }} />
                  <Bar dataKey="alerts" name="Alerts" fill={t.accent} radius={[0, 3, 3, 0]} />
                  <Bar dataKey="escalated" name="Escalated" fill={t.amber} radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card t={t} title="Settled value by chain" note={`${period} · ${usd(totalChain)} total`}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 150, height: 150, flex: "0 0 auto" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={data.chains} dataKey="value" nameKey="name" innerRadius={44} outerRadius={70} paddingAngle={2} stroke="none">
                      {data.chains.map((c, i) => <Cell key={c.name} fill={chainColors[i % chainColors.length]} />)}
                    </Pie>
                    <Tooltip content={<ChartTip t={t} unit="M" />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div style={{ flex: 1, display: "grid", gap: 6 }}>
                {data.chains.map((c, i) => (
                  <div key={c.name} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ width: 9, height: 9, borderRadius: 2, background: chainColors[i % chainColors.length] }} />
                    <span style={{ fontSize: 12 }}>{c.name}</span>
                    <span style={{ marginLeft: "auto", fontFamily: t.mono, fontSize: 12, color: t.muted }}>${c.value.toFixed(1)}M</span>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ marginTop: 12, borderTop: `1px solid ${t.lineSoft}`, paddingTop: 12 }}>
              <Eyebrow t={t}>Asset mix</Eyebrow>
              <div style={{ display: "flex", height: 10, borderRadius: 5, overflow: "hidden", marginTop: 8 }}>
                {data.assets.map((a, i) => (
                  <div key={a.name} title={`${a.name} ${a.pct}%`}
                    style={{ width: `${a.pct}%`, background: [t.accent, t.amber, t.inkSoft][i % 3] }} />
                ))}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontFamily: t.mono, fontSize: 11, color: t.muted }}>
                {data.assets.map((a) => <span key={a.name}>{a.name} {a.pct}%</span>)}
              </div>
            </div>
          </Card>
        </div>

        <div className="idz-block">
          <Card t={t} title="Counterparty exposure" note="Ranked by settled value, with tainted share and current posture" flush>
            <div className="idz-scroll">
              <table className="idz-table" style={{ minWidth: 820 }}>
                <thead>
                  <tr>
                    {["Counterparty", "Type", "Jur.", "Chain", "Settled", "Tainted share", "Score", "Posture"].map((h, i) => (
                      <th key={h} className={i > 3 ? "idz-num" : ""}>
                        <Eyebrow t={t}>{h}</Eyebrow>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.counterparties.map((r) => (
                    <tr key={r.id} onClick={() => onSelectCounterparty && onSelectCounterparty(r)}
                      style={{ cursor: onSelectCounterparty ? "pointer" : "default" }}>
                      <td style={{ fontSize: 12.5, fontWeight: 600 }}>{r.name}</td>
                      <td style={{ fontSize: 12, color: t.muted }}>{r.type}</td>
                      <td style={{ fontFamily: t.mono, fontSize: 12, color: t.muted }}>{r.jurisdiction}</td>
                      <td style={{ fontSize: 12, color: t.muted }}>{r.chain}</td>
                      <td className="idz-num" style={{ fontFamily: t.mono, fontSize: 12.5 }}>${r.volume.toFixed(1)}M</td>
                      <td className="idz-num" style={{ fontFamily: t.mono, fontSize: 12.5, color: r.taintedPct > 1 ? t.red : t.text }}>
                        {r.taintedPct.toFixed(2)}%
                      </td>
                      <td className="idz-num">
                        <span className="idz-figure" style={{ fontFamily: t.mono, fontSize: 13, color: scoreColor(r.score, t) }}>{r.score}</span>
                        <span style={{ marginLeft: 8 }}><Delta v={r.trend} t={t} /></span>
                      </td>
                      <td className="idz-num">
                        <Pill t={t} tone={r.posture === "Held" ? "red" : r.posture === "Restricted" ? "amber" : r.posture === "Watch" ? "neutral" : "green"}>
                          {r.posture}
                        </Pill>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        <div className="idz-block">
          <Card t={t} title="Payout hold queue"
            note={cell ? `Filtered to ${cell.category.toLowerCase()} at ${data.hops[cell.col].toLowerCase()}` : "Held before settlement, awaiting disposition"}
            right={cell
              ? <button className="idz-btn-ghost idz-no-print" onClick={() => setCell(null)} style={{ color: t.accent, fontFamily: t.sans }}>Clear filter</button>
              : <Pill t={t} tone={openHolds.length ? "red" : "green"}>{openHolds.length} open</Pill>}
            flush>
            {visibleHolds.length === 0 ? (
              <div className="idz-empty">
                <FiCheckCircle size={22} color={t.green} aria-hidden="true" />
                <p style={{ fontSize: 13, fontWeight: 600, margin: "8px 0 2px" }}>
                  {cell ? "Nothing held in this category" : "Queue is clear"}
                </p>
                <p style={{ fontSize: 12, color: t.muted, margin: 0 }}>
                  {cell ? "Exposure here has not produced a stopped payout. Clear the filter to see the rest of the queue."
                        : "Nothing is waiting on a decision. New holds appear here the moment a transfer is stopped."}
                </p>
              </div>
            ) : (
              <div className="idz-scroll">
                <table className="idz-table" style={{ minWidth: 900 }}>
                  <thead>
                    <tr>
                      {["Hold", "Counterparty", "Program", "Amount", "Reason", "Age", "Score", "Action"].map((h, i) => (
                        <th key={h} className={i === 3 ? "idz-num" : i === 7 ? "idz-num" : ""}>
                          <Eyebrow t={t}>{h === "Action" ? "" : h}</Eyebrow>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleHolds.map((h) => (
                      <tr key={h.id}>
                        <td style={{ fontFamily: t.mono, fontSize: 12, color: t.muted }}>{h.id}</td>
                        <td style={{ fontSize: 12.5, fontWeight: 600 }}>
                          {h.counterparty}<span style={{ color: t.muted, fontWeight: 400 }}> · {h.chain}</span>
                        </td>
                        <td style={{ fontSize: 12, color: t.muted }}>{h.program}</td>
                        <td className="idz-num" style={{ fontFamily: t.mono, fontSize: 12.5 }}>
                          {money(h.amountUsd)} <span style={{ color: t.muted }}>{h.asset}</span>
                        </td>
                        <td style={{ fontSize: 12 }}>{h.reason}</td>
                        <td style={{ fontFamily: t.mono, fontSize: 12, color: t.muted }}>{h.age}</td>
                        <td><span className="idz-figure" style={{ fontFamily: t.mono, fontSize: 13, color: scoreColor(h.score, t) }}>{h.score}</span></td>
                        <td className="idz-num idz-no-print">
                          <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                            <button className="idz-btn" onClick={() => act(h, "release")}
                              style={{ background: t.greenSoft, color: t.green, fontFamily: t.sans }}>Release</button>
                            <button className="idz-btn" onClick={() => act(h, "escalate")}
                              style={{ background: t.redSoft, color: t.red, fontFamily: t.sans }}>Escalate</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>

        <div className="idz-block idz-card" style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, padding: "12px 16px" }}>
          <FiFileText size={15} color={t.accent} aria-hidden="true" />
          <span style={{ fontSize: 12.5 }}>
            Every screening result, score and disposition on this page is written to the evidence file with its
            rationale, the policy version in force and the operator who acted.
          </span>
          <button className="idz-btn idz-no-print" onClick={onOpenEvidence}
            style={{ marginLeft: "auto", background: t.ink, color: "#FFFFFF", fontFamily: t.sans, fontSize: 12,
              padding: "7px 12px", display: "inline-flex", alignItems: "center", gap: 4 }}>
            Open evidence file <FiChevronRight size={14} aria-hidden="true" />
          </button>
        </div>
      </main>
    </div>
  );
}
